import React from 'react';
import { View, StyleSheet, useWindowDimensions } from 'react-native';
import { RootStackParamList } from '../types';
import { NavigationContainer } from '@react-navigation/native';
import { createNativeStackNavigator } from '@react-navigation/native-stack';
import { useAuth } from '../context/AuthContext';
import { LoginScreen } from '../screens/LoginScreen';
import { AccessDeniedScreen } from '../screens/AccessDeniedScreen';
import { ACCESS_DENIED_COPY, deniedReason } from '../utils/portalAccess';
import { HomeScreen } from '../screens/HomeScreen';
import { AttendanceScreen } from '../screens/AttendanceScreen';
import { AcademicsScreen } from '../screens/AcademicsScreen';
import { LeavesScreen } from '../screens/LeavesScreen';
import { EarlyPickupScreen } from '../screens/EarlyPickupScreen';
import { DocumentsScreen } from '../screens/DocumentsScreen';
import { DocumentRequestDetailScreen } from '../screens/DocumentRequestDetailScreen';
import { StoreScreen } from '../screens/StoreScreen';
import { WalletScreen } from '../screens/WalletScreen';
import { CanteenScreen } from '../screens/CanteenScreen';
import { MoreScreen } from '../screens/MoreScreen';
import { NotificationsScreen } from '../screens/NotificationsScreen';
import { ComplaintsScreen } from '../screens/ComplaintsScreen';
import { FeeScreen } from '../screens/FeeScreen';
import { ExamResultsScreen } from '../screens/ExamResultsScreen';
import { TimetableScreen } from '../screens/TimetableScreen';
// Transport is hidden until the backend has a real transport module — the screen
// only had placeholder data. Kept (not deleted) so it can be switched back on.
// import { TransportScreen } from '../screens/TransportScreen';
import { EventsScreen } from '../screens/EventsScreen';
import { ProfileScreen } from '../screens/ProfileScreen';
import { LiveClassScreen } from '../screens/LiveClassScreen';
import { CommunicationScreen } from '../screens/CommunicationScreen.web';
import { ChatScreen } from '../screens/ChatScreen';
import { StudentIdScreen } from '../screens/StudentIdScreen';
import { SettingsScreen } from '../screens/SettingsScreen';
import { HomeworkDetailScreen } from '../screens/HomeworkDetailScreen';
import { HomeworkScreen } from '../screens/HomeworkScreen';
import { ProjectScreen } from '../screens/ProjectScreen';
import { ProjectDetailScreen } from '../screens/ProjectDetailScreen';
import { ExamScheduleScreen } from '../screens/ExamScheduleScreen';
import { ReportCardDetailScreen } from '../screens/ReportCardDetailScreen';
import { CxcRegistrationScreen } from '../screens/CxcRegistrationScreen';
import { WebSidebar } from '../components/common/WebSidebar';
import { LoadingSpinner } from '../components/common/LoadingSpinner';
import { SidebarProvider, useSidebar } from '../context/SidebarContext';
import { rootNavigationRef } from './rootNavigation';
import { MeetingsScreen } from '../screens/MeetingsScreen';
import { AnimatePresence } from '../components/motion/Motion';
import { usesMenuDrawer } from '../utils/webLayout';

// The shared ref (rootNavigation.ts) keeps its old export name for WebSidebar.
export const navigationRef = rootNavigationRef;

const Stack = createNativeStackNavigator<RootStackParamList>();

export function AppNavigator() {
  const { isLoading, isAuthenticated, user, authError, students, logout } = useAuth();
  const [currentRoute, setCurrentRoute] = React.useState('Home');

  // Covers the whole sign-in chain (token → /User/me → /Parent/portal), so nothing
  // below renders on partial data.
  if (isLoading) {
    // The crest on the navy band, full screen: the first thing a parent sees after signing in.
    return <LoadingSpinner fullScreen message="Loading your portal..." />;
  }

  // Not invited, switched off, nothing linked, or access unknown: Access Denied,
  // saying which, with a way out to sign in with the invited email.
  const denied = isAuthenticated ? deniedReason(authError, user, students, 'web') : null;
  if (denied) {
    return <AccessDeniedScreen {...ACCESS_DENIED_COPY[denied]} onSignOut={logout} />;
  }

  // Fee/installment selection and profile completion are both handled as non-blocking
  // popups shown over the Home screen (see FeeOnboardingModal / ProfileOnboardingModal)
  // rather than full-screen gates, so the main app always mounts once authenticated.
  return (
    <SidebarProvider>
      <AppNavigatorContent
        isAuthenticated={isAuthenticated}
        currentRoute={currentRoute}
        setCurrentRoute={setCurrentRoute}
      />
    </SidebarProvider>
  );
}

function AppNavigatorContent({ isAuthenticated, currentRoute, setCurrentRoute }: any) {
  const { width } = useWindowDimensions();
  const { isSidebarOpen, closeSidebar } = useSidebar();
  // Phones and tablets: the menu is a drawer, so the page gets the whole width (utils/webLayout).
  const isMobile = usesMenuDrawer(width);

  return (
    <NavigationContainer
      ref={navigationRef}
      onStateChange={(state) => {
        if (state) {
          const route = state.routes[state.index];
          setCurrentRoute(route.name);
        }
      }}
    >
      {isAuthenticated ? (
        <View style={{ flex: 1, flexDirection: 'row' }}>
          {!isMobile && <WebSidebar currentRoute={currentRoute} />}
          {/* Kept mounted while the drawer slides back out. */}
          <AnimatePresence>
            {isMobile && isSidebarOpen && (
              <WebSidebar key="drawer" currentRoute={currentRoute} isOverlay={true} onClose={closeSidebar} />
            )}
          </AnimatePresence>
          <View style={{ flex: 1 }}>
            <Stack.Navigator
              screenOptions={{
                headerShown: false,
                contentStyle: { backgroundColor: 'transparent' },
              }}>
              <Stack.Screen name="Home" component={HomeScreen} />
              <Stack.Screen name="Attendance" component={AttendanceScreen} />
              <Stack.Screen name="Academics" component={AcademicsScreen} />
              <Stack.Screen name="Leaves" component={LeavesScreen} />
              <Stack.Screen name="EarlyPickup" component={EarlyPickupScreen} />
              <Stack.Screen name="Documents" component={DocumentsScreen} />
              <Stack.Screen name="DocumentRequestDetail" component={DocumentRequestDetailScreen} />
              <Stack.Screen name="Store" component={StoreScreen} />
              <Stack.Screen name="Wallet" component={WalletScreen} />
              <Stack.Screen name="Canteen" component={CanteenScreen} />
              <Stack.Screen name="More" component={MoreScreen} />
              <Stack.Screen name="Notifications" component={NotificationsScreen} />
              <Stack.Screen name="Meetings" component={MeetingsScreen} />
              <Stack.Screen name="Complaints" component={ComplaintsScreen} />
              <Stack.Screen name="Fees" component={FeeScreen} />
              <Stack.Screen name="CxcRegistration" component={CxcRegistrationScreen} />
              <Stack.Screen name="ExamResults" component={ExamResultsScreen} />
              <Stack.Screen name="Timetable" component={TimetableScreen} />
              {/* <Stack.Screen name="Transport" component={TransportScreen} /> */}
              <Stack.Screen name="Events" component={EventsScreen} />
              <Stack.Screen name="Profile" component={ProfileScreen} />
              <Stack.Screen name="LiveClass" component={LiveClassScreen} />
              <Stack.Screen name="Communication" component={CommunicationScreen} />
              <Stack.Screen name="Chat" component={ChatScreen} />
              <Stack.Screen name="StudentId" component={StudentIdScreen} />
              <Stack.Screen name="Settings" component={SettingsScreen} />
              <Stack.Screen name="Homework" component={HomeworkScreen} />
              <Stack.Screen name="HomeworkDetail" component={HomeworkDetailScreen} />
              <Stack.Screen name="Projects" component={ProjectScreen} />
              <Stack.Screen name="ProjectDetail" component={ProjectDetailScreen} options={{ presentation: 'transparentModal' }} />
              <Stack.Screen name="ExamSchedule" component={ExamScheduleScreen} options={{ presentation: 'transparentModal' }} />
              <Stack.Screen name="ReportCardDetail" component={ReportCardDetailScreen} />
            </Stack.Navigator>
          </View>
        </View>
      ) : (
        <Stack.Navigator
          screenOptions={{
            headerShown: false,
            contentStyle: { backgroundColor: 'transparent' },
          }}>
          <Stack.Screen name="Login" component={LoginScreen} />
        </Stack.Navigator>
      )}
    </NavigationContainer>
  );
}
